import { CardContent } from '@mui/material'
import { Spacer, Item } from '../../styles'
import { useTranslation } from 'react-i18next'
import React from 'react'
import { formatDate } from '../formatDate'
import { StyledView, StyledTypography } from '../common/styles'
import { formatBooleanValue } from '../../../../../services/utils/formatters'

/**
 * A component that displays delivery point details for consumer contracts
 *
 * @param {object} contract - Contains the consumer's contract data from the new API
 * @param {string} contract.energyPreference - Energy type preference (SOLAR, WIND, HYDRO, etc.)
 * @param {object} contract.plantAddress - The delivery point address information
 * @param {string} contract.plantAddress.street - Street name for the delivery point
 * @param {string} contract.plantAddress.houseNumber - House number for the delivery point
 * @param {string} contract.plantAddress.postalCode - Postal/zip code for the delivery point
 * @param {string} contract.plantAddress.city - City where the delivery point is located
 * @param {string} contract.marketLocation - Market location identifier
 * @param {string} contract.meterNumber - Meter identification number
 * @param {string} contract.lastUtilitySupplierName - Name of previous energy supplier
 * @param {string} contract.utilityContractNumber - Customer number with previous supplier
 * @param {string} contract.startOfDeliveryDate - Date when energy delivery starts
 * @param {boolean} contract.isStartOfDeliveryNextPossibleDate - Whether to use next possible date for delivery
 */
function ConsumerGenerationPlantDetails({ contract }) {
  const { t } = useTranslation()

  return (
    <>
      <StyledView>
        <CardContent>
          <StyledTypography fontWeight="bold" variant="h4" gutterBottom>
            {t('LBLDeliveryPoint')}
          </StyledTypography>
          <Spacer mb={4} />
          <StyledTypography variant="body2" component="div" gutterBottom>
            {/* Energy Preference field */}
            <Item>
              <StyledTypography fontWeight="fontWeightMedium">
                {t('LBLPreference')}
              </StyledTypography>
              <StyledTypography fontWeight="fontWeightRegular" align="right">
                {contract?.energyPreference
                  ? t(contract.energyPreference)
                  : '--'}
              </StyledTypography>
            </Item>

            {/* Delivery Point Address field */}
            <Item>
              <StyledTypography fontWeight="fontWeightMedium">
                {t('LBLPlantAdress')}
              </StyledTypography>
              <StyledTypography fontWeight="fontWeightRegular" align="right">
                {contract?.plantAddress ? (
                  <>
                    {contract.plantAddress.street &&
                      contract.plantAddress.houseNumber &&
                      `${contract.plantAddress.street} ${contract.plantAddress.houseNumber}`}
                    <br />
                    {contract.plantAddress.postalCode &&
                      contract.plantAddress.city &&
                      `${contract.plantAddress.postalCode} ${contract.plantAddress.city}`}
                  </>
                ) : (
                  '--'
                )}
              </StyledTypography>
            </Item>

            {/* Start of Delivery field */}
            <Item>
              <StyledTypography fontWeight="fontWeightMedium">
                {t('LBLStartOfDeliveryDate')}
              </StyledTypography>
              <StyledTypography fontWeight="fontWeightRegular" align="right">
                {contract?.isStartOfDeliveryNextPossibleDate
                  ? t('NextPossibleDate')
                  : contract?.startOfDeliveryDate
                  ? formatDate(contract.startOfDeliveryDate)
                  : '--'}
              </StyledTypography>
            </Item>

            {/* Market Location field */}
            <Item>
              <StyledTypography fontWeight="fontWeightMedium">
                {t('LBLMarketLocation')}
              </StyledTypography>
              <StyledTypography fontWeight="fontWeightRegular" align="right">
                {contract?.marketLocation || '--'}
              </StyledTypography>
            </Item>

            {/* Meter Number field */}
            <Item>
              <StyledTypography fontWeight="fontWeightMedium">
                {t('LBLMeterNumber')}
              </StyledTypography>
              <StyledTypography fontWeight="fontWeightRegular" align="right">
                {contract?.meterNumber || '--'}
              </StyledTypography>
            </Item>

            {/* Last Customer Number field */}
            <Item>
              <StyledTypography fontWeight="fontWeightMedium">
                {t('LBLLastCustomerNum')}
              </StyledTypography>
              <StyledTypography fontWeight="fontWeightRegular" align="right">
                {contract?.utilityContractNumber || '--'}
              </StyledTypography>
            </Item>

            {/* Last Utility Supplier field */}
            <Item>
              <StyledTypography fontWeight="fontWeightMedium">
                {t('LBLLastSupplier')}
              </StyledTypography>
              <StyledTypography fontWeight="fontWeightRegular" align="right">
                {contract?.currentUtilitySupplierName || '--'}
              </StyledTypography>
            </Item>

            {/* Next Possible Date field */}
            <Item>
              <StyledTypography fontWeight="fontWeightMedium">
                {t('LBLNextPossibleDate')}
              </StyledTypography>
              <StyledTypography fontWeight="fontWeightRegular" align="right">
                {formatBooleanValue(
                  contract?.isStartOfDeliveryNextPossibleDate,
                  t
                )}
              </StyledTypography>
            </Item>
          </StyledTypography>
        </CardContent>
      </StyledView>
    </>
  )
}

export default ConsumerGenerationPlantDetails
